/* eslint-disable react/forbid-prop-types */
import React from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import VoicebaseReactPlayerContext from "./VoicebaseReactPlayerContext";
import Icon from "./common/Icon";
import Spin from "./common/Spin";
import moment from "moment";
import S from "./styles/components";
import PanelStack from "./common/Panel/PanelStack";
// eslint-disable-next-line max-len
import analyticsParser, {
  ANALYTICS_SCHEMA_VERSION_V3,
  AVAILABLE_ANALYTICS_SCHEMAS,
} from "./utilities/analytics-parser";
import appStateFactory from "./utilities/appStateFactory";
import { KEYWORDS_TAB } from "./common/playerConstants";

import Player from "./Player";
import ToastContainer from "./Toaster/ToastContainer";

function parseExternalHits(externalHits) {
  if (!externalHits) {
    return [];
  }
  if (typeof externalHits === "string") {
    try {
      return parseExternalHits(JSON.parse(externalHits));
    } catch (err) {
      return [];
    }
  }
  return Array.isArray(externalHits) ? externalHits : [];
}

const DEFAULT_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'";

const fullSize = {
  display: "flex",
  flex: "auto",
  flexDirection: "column",
  minHeight: 0,
  background: "#f0f2f5",
  width: "100%",
  height: "100%",
};

const center = {
  display: "flex",
  flexGrow: 1,
  flexDirection: "column",
  width: "100%",
  height: "100%",
  justifyContent: "center",
  alignItems: "stretch",
};

const headerStyle = {
  height: "64px",
  padding: "0 50px",
  lineHeight: "64px",
  background: "white",
  color: "black",
  textAlign: "center",
  fontFamily: DEFAULT_FONT,
};

const contentStyle = {
  flex: "auto",
  minHeight: 0,
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "stretch",
  fontFamily: DEFAULT_FONT,
};

const errIconStyle = {
  color: "red",
  margin: "1rem",
  fontSize: "2.25rem",
  textAlign: "center",
};

/**
 *
 */
class VoicebaseReactPlayer extends React.Component {
  static contextType = VoicebaseReactPlayerContext;

  static propTypes = {
    // Input Props
    analyticsFormat: PropTypes.oneOf(AVAILABLE_ANALYTICS_SCHEMAS),
    mediaUrl: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    children: PropTypes.any,
    startTime: PropTypes.number,
    advanceToFirstHit: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),

    // Mapped Props
    analytics: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.array,
      PropTypes.bool,
    ]),
  };

  constructor(props) {
    super(props);
    this.toastRef = React.createRef(); // Create ref for ToastContainer
  }
  // static defaultProps = {
  //   analyticsFormat: ANALYTICS_SCHEMA_VERSION_V3,
  //   mediaUrl: "",
  //   analytics: false,
  //   loading: false,
  //   error: false,
  //   startTime: 0,
  //   children: "",
  //   advanceToFirstHit: 0,
  // };

  appFromAnalytics(media) {
    const {
      analyticsFormat = ANALYTICS_SCHEMA_VERSION_V3,
      loading = false,
      error = false,
      mediasToDisplay,
      analytics,
      mediaUrl = "",
      externalHits,
      startTime = 0,
      advanceToFirstHit = 0,
    } = media;
    const analyticsProps = analyticsParser(analyticsFormat, analytics);
    const appState = appStateFactory(analyticsProps);
    appState.view = {
      activeTab: KEYWORDS_TAB,
    };
    appState.startTime = startTime;
    appState.advanceToFirstHit = advanceToFirstHit;

    appState.externalHits = externalHits;
    get(appState, "player", {}).mediaUrl = mediaUrl;
    return appState;
  }

  /**
   * accepts:
   * 1. a string
   * 2. an element
   * 3. an array of strings or elements
   */
  childrenToMessage(children) {
    if (!Array.isArray(children)) return this.childToMessage(children);
    return children ? children.map(this.childToMessage) : "";
  }

  childToMessage(child) {
    if (!child) return "";
    if (React.isValidElement(child)) return child;
    if (typeof child === "string") return <p>{child}</p>;
    return "";
  }

  showToast = (message, type) => {
    this.toastRef.current.addToast(message, type);
  };

  render() {
    const {
      loading,
      error,
      children,
      isPCIFlagEnabled,
      showPCIFlag,
      handlePCIFlagClick,
      flagAction,
      handleMinimizePlayer,
      hidePlayer,
      medias,
      showPlayer,
      updateLastPlayed,
      setActiveMedia,
      mediasToDisplay = [],
      isHostedApp = false,
      exportTranscript,
      mediaDownloadResponse
    } = this.props;
   
    if (loading) {
      return (
        <div style={fullSize}>
          <ToastContainer ref={this.toastRef} />
          <div style={headerStyle}>
            <h2 style={{ margin: 0 }}>Analytics Studio Player.</h2>
          </div>
          <div style={contentStyle}>
            <div style={center}>
              <Spin size="large" style={{ margin: "1rem" }} />
              {this.childToMessage(children)}
            </div>
          </div>
        </div>
      );
    }

    if (error) {
      const message = this.childrenToMessage(React.Children.toArray(children));
      return (
        <div style={fullSize}>
          <ToastContainer ref={this.toastRef} />
          <div style={headerStyle}>
            <h2>Analytics Studio Player</h2>
          </div>
          <div style={contentStyle}>
            <div style={center}>
              <Icon
                type="exclamation-circle"
                theme="filled"
                size="huge"
                style={errIconStyle}
              />
              {message}
            </div>
          </div>
        </div>
      );
    }

    const simpleMediaPlayer = (media) => {
      const appState = this.appFromAnalytics(media);
      const interactionType = get(
        appState,
        "parsedFromAnalytics.interactionType"
      );
      const mediaId = get(appState, "parsedFromAnalytics.mediaId");
      const transcriptLength = media?.analytics?.length;
      return (
        <>
        <ToastContainer ref={this.toastRef} />
        <Player
          {...appState}
          key={mediaId}
          isPCIFlagEnabled={isPCIFlagEnabled}
          showPCIFlag={showPCIFlag}
          handlePCIFlagClick={handlePCIFlagClick}
          flagAction={flagAction}
          handleMinimizePlayer={handleMinimizePlayer}
          hidePlayer={hidePlayer}
          transcriptLength={transcriptLength}
          showToast={this.showToast}
        />
        </>
        
      );
    };

    return (
      <VoicebaseReactPlayerContext.Provider
        value={{
          medias,
          showPlayer,
          updateLastPlayed,
          setActiveMedia,
          isHostedApp,
          exportTranscript,
          mediaDownloadResponse
        }}
      >
        {mediasToDisplay?.length ? (
          <S.GridCell stretch>
            <PanelStack isHostedApp={isHostedApp}>
              {mediasToDisplay?.map((media, index) => {
                const callStartTime =
                  moment(
                    media.analytics.metadata.callDetails?.callStartTime
                  ).format("ll") || "unknown";

                const agentName =
                  media.analytics.metadata?.callDetails?.agent?.fullName ||
                  "unknown";

                let title = `${agentName} on ${callStartTime}`;

                const appState = this.appFromAnalytics(media);
                const mediaId = get(appState, "parsedFromAnalytics.mediaId");
                const transcriptLength = media?.analytics?.length;

                return (
                  <>
                  <ToastContainer ref={this.toastRef} />
                  <Player
                    {...appState}
                    key={mediaId}
                    isPCIFlagEnabled={isPCIFlagEnabled}
                    showPCIFlag={showPCIFlag}
                    handlePCIFlagClick={handlePCIFlagClick}
                    flagAction={flagAction}
                    handleMinimizePlayer={handleMinimizePlayer}
                    hidePlayer={hidePlayer}
                    title={title}
                    isPlayer={true}
                    transcriptLength={transcriptLength}
                    showToast={this.showToast}
                  />
                  </>
                );
              })}
            </PanelStack>
          </S.GridCell>
        ) : (
          simpleMediaPlayer(this.props)
        )}
      </VoicebaseReactPlayerContext.Provider>
    );
  }
}

export default VoicebaseReactPlayer;
